// @core/stores/attachment/AttachmentStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { attachmentApi } from '@core/api';
import type {
  AbortMultipartUploadRequest,
  Attachment,
  AttachmentResponse,
  CompleteMultipartUploadRequest,
  ConfirmUploadRequest,
  DeleteAttachmentsBulkResponse,
  GetDownloadUrlResponse,
  GetDownloadUrlsBulkRequest,
  GetDownloadUrlsBulkResponse,
  GetMultipartUploadPartUrlRequest,
  GetMultipartUploadPartUrlResponse,
  GetOwnershipHistoryResponse,
  GetProcessingStatusResponse,
  GetUploadUrlRequest,
  GetUploadUrlResponse,
  InitiateMultipartUploadRequest,
  InitiateMultipartUploadResponse,
  ListAttachmentsByIDsRequest,
  ListAttachmentsResponse,
  RetryProcessingRequest,
  TransferOwnershipRequest,
  UpdateAttachmentRequest,
} from '@core/types/attachment';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class AttachmentStore {
  // List state
  items: Attachment[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Attachment | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Attachment>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Attachment | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Attachment | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Abort multipart upload
   */
  async AbortMultipartUpload(data: AbortMultipartUploadRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await attachmentApi.AbortMultipartUpload(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Complete multipart upload
   */
  async CompleteMultipartUpload(data: CompleteMultipartUploadRequest): Promise<AttachmentResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await attachmentApi.CompleteMultipartUpload(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Confirm upload and trigger processing
   */
  async ConfirmUpload(data: ConfirmUploadRequest): Promise<AttachmentResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await attachmentApi.ConfirmUpload(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Delete attachment
   */
  async DeleteAttachment(attachment_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await attachmentApi.DeleteAttachment(attachment_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== attachment_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === attachment_id) {
          this.currentItem = null;
        }

        this.removeFromCache(attachment_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete multiple attachments
   */
  async DeleteAttachmentsBulk(): Promise<DeleteAttachmentsBulkResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await attachmentApi.DeleteAttachmentsBulk();

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get attachment by ID
   */
  async GetAttachment(attachment_id: string): Promise<Attachment> {
    // Check cache first
    const cached = this.cache.get(attachment_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await attachmentApi.GetAttachment(attachment_id);
      const item = response.attachment;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get presigned download URL
   */
  async GetDownloadUrl(attachment_id: string): Promise<GetDownloadUrlResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await attachmentApi.GetDownloadUrl(attachment_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get presigned download URLs for multiple attachments
   */
  async GetDownloadUrlsBulk(data: GetDownloadUrlsBulkRequest): Promise<GetDownloadUrlsBulkResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await attachmentApi.GetDownloadUrlsBulk(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get presigned URL for multipart upload part
   */
  async GetMultipartUploadPartUrl(data: GetMultipartUploadPartUrlRequest): Promise<GetMultipartUploadPartUrlResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await attachmentApi.GetMultipartUploadPartUrl(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get attachment ownership history
   */
  async GetOwnershipHistory(attachment_id: string): Promise<GetOwnershipHistoryResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await attachmentApi.GetOwnershipHistory(attachment_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get attachment processing status
   */
  async GetProcessingStatus(attachment_id: string): Promise<GetProcessingStatusResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await attachmentApi.GetProcessingStatus(attachment_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get presigned URL for file upload
   */
  async GetUploadUrl(data: GetUploadUrlRequest): Promise<GetUploadUrlResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await attachmentApi.GetUploadUrl(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Initiate multipart upload for large files
   */
  async InitiateMultipartUpload(data: InitiateMultipartUploadRequest): Promise<InitiateMultipartUploadResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await attachmentApi.InitiateMultipartUpload(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List attachments with filters
   */
  async ListAttachments(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await attachmentApi.ListAttachments(params);

      runInAction(() => {
        this.items = response.attachments?.attachments ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get attachments by list of IDs
   */
  async ListAttachmentsByIDs(data: ListAttachmentsByIDsRequest): Promise<ListAttachmentsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await attachmentApi.ListAttachmentsByIDs(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List attachments by owner
   */
  async ListAttachmentsByOwner(owner_id: string, params?: PaginatedRequest & { type?: string | number | boolean; }): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await attachmentApi.ListAttachmentsByOwner(owner_id, params);

      runInAction(() => {
        this.items = response.attachments?.attachments ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Retry failed attachment processing
   */
  async RetryProcessing(attachment_id: string, data: RetryProcessingRequest): Promise<AttachmentResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await attachmentApi.RetryProcessing(attachment_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Transfer attachment ownership
   */
  async TransferOwnership(attachment_id: string, data: TransferOwnershipRequest): Promise<AttachmentResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await attachmentApi.TransferOwnership(attachment_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update attachment metadata
   */
  async UpdateAttachment(attachment_id: string, data: UpdateAttachmentRequest): Promise<Attachment> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await attachmentApi.UpdateAttachment(attachment_id, data);
      const item = response.attachment;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const attachmentStore = new AttachmentStore();
